import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class GameService {
  geoServiceSubject$ = new Subject();
  isPartnerGame = false;
  partner;
  partnerDetails;
  activeGame;
  pageConfig = {
    g: null,
    p: null
  }
  endQuest = {
      isVisible: false,
      coordinates: [],
      lat: null,
      lng: null
  }
  mapPlayers = [];
  enemyMarkers = [];
  originalQuests = [];
  battleCircle  = {
    "type": "geojson",
    "data": {
        "type": "Feature",
        "geometry": {
          "type": "Polygon",
          "coordinates": []
        }
    }
  }
  interimSettings = {
    doubleActive: false,
    freezeActive: false,
    mapTapActive: false,
  }
  me: any = {
    _id: null,
    position: null,
    points: 0,
    backpack: [],
    ranking: null,
    name: null,
    isFinished: false,
    passedQuests: [],
    perkEvents: []
  };

  /**
   * Host/auto-awarded photo points live on the server (photos[].points where isValidated).
   * The server re-adds them on every sync, so me.points sent to the server must NOT include
   * them. Predicate and numeric coercion mirror the server's reconcile logic exactly.
   */
  sumValidatedPhotoPoints(photos: any[]): number {
    return (photos || []).reduce((sum, photo) => {
      if (photo?.isValidated !== true) {
        return sum;
      }
      const points = Number(photo.points);
      return Number.isFinite(points) ? sum + points : sum;
    }, 0);
  }

  /** Points the app owns locally (quizzes, perks), i.e. total minus server-owned photo points. */
  getLocallyEarnedPoints(): number {
    const local = Number(this.me?.points || 0) - this.sumValidatedPhotoPoints(this.me?.photos);
    return Math.max(0, local);
  }

  private ensureAlignedProgressArrays(): void {
    if (!Array.isArray(this.me?.passedQuests)) {
      return;
    }
    if (!Array.isArray(this.me.questionStatuses)) {
      this.me.questionStatuses = [];
    }
    if (!Array.isArray(this.me.questionAnswers)) {
      this.me.questionAnswers = [];
    }
    while (this.me.questionStatuses.length < this.me.passedQuests.length) {
      this.me.questionStatuses.push(0);
    }
    while (this.me.questionAnswers.length < this.me.passedQuests.length) {
      this.me.questionAnswers.push('');
    }
  }

  /**
   * Prefer non-empty local progress arrays over empty server arrays.
   * Server responses (updatePlayerOne / usePerk / getPlayerByTeamName) often return
   * questionStatuses: [] even when the client already recorded answers — replacing
   * `me` wholesale then causes later syncs to omit statuses entirely.
   */
  private pickProgressArray(localArr: any, remoteArr: any, fill: any, targetLen: number): any[] {
    const local = Array.isArray(localArr) ? localArr : [];
    const remote = Array.isArray(remoteArr) ? remoteArr : [];
    let chosen: any[];
    if (remote.length === 0 && local.length > 0) {
      chosen = [...local];
    } else if (local.length === 0 && remote.length > 0) {
      chosen = [...remote];
    } else if (remote.length >= local.length) {
      chosen = [...remote];
    } else {
      chosen = [...local];
    }
    while (chosen.length < targetLen) {
      chosen.push(fill);
    }
    return chosen;
  }

  /**
   * Merge a server player snapshot into local `me` without dropping quiz progress
   * the client already recorded (statuses / answers / ahead passedQuests).
   */
  applyServerPlayer(remote: any): void {
    if (!remote) {
      return;
    }
    const local = this.me || {};
    const localPassed = Array.isArray(local.passedQuests) ? local.passedQuests : [];
    const remotePassed = Array.isArray(remote.passedQuests) ? remote.passedQuests : [];
    const localAhead = localPassed.length > remotePassed.length;
    const passedQuests = localAhead ? [...localPassed] : [...remotePassed];

    const questionStatuses = this.pickProgressArray(
      local.questionStatuses,
      remote.questionStatuses,
      0,
      passedQuests.length
    );
    const questionAnswers = this.pickProgressArray(
      local.questionAnswers,
      remote.questionAnswers,
      '',
      passedQuests.length
    );

    // Local ahead (answered, sync not reflected yet): keep the higher total.
    // Otherwise take server total (includes validated photo awards / perk spend).
    const localPoints = Number(local.points) || 0;
    const remotePoints = remote.points != null ? Number(remote.points) || 0 : localPoints;
    const points = localAhead ? Math.max(localPoints, remotePoints) : remotePoints;

    this.me = {
      ...local,
      ...remote,
      passedQuests,
      questionStatuses,
      questionAnswers,
      points,
      photos: remote.photos != null ? remote.photos : local.photos,
      // Keep fresher on-device GPS; server position is often stale between polls.
      position: local.position || remote.position,
      backpack: remote.backpack != null ? remote.backpack : local.backpack,
      perkEvents: remote.perkEvents != null ? remote.perkEvents : local.perkEvents,
    };
  }

  /**
   * Records a quiz result index-aligned with passedQuests so the server can rebuild
   * questionStatuses / questionAnswers on sync. status is numeric (1 = correct, matching
   * the admin's "From correct answers" calculation). Never guesses previous answers as
   * correct: unknown historical slots are padded with 0 to keep the arrays index-aligned.
   */
  recordQuestionResult(questionId: string, status: number, answer: any): void {
    if (!questionId || !Array.isArray(this.me?.passedQuests)) {
      return;
    }
    // Normalize id match — server/local ids may differ by string vs ObjectId-like value.
    const idx = this.me.passedQuests.findIndex(
      (id) => String(id) === String(questionId)
    );
    if (idx === -1) {
      return;
    }
    this.ensureAlignedProgressArrays();
    this.me.questionStatuses[idx] = Number(status) || 0;
    this.me.questionAnswers[idx] = answer != null ? answer : '';
  }

  /** Player payload for point-reconciling endpoints (updatePlayerOne / updatePlayerStress). */
  buildSyncPlayer(): any {
    this.ensureAlignedProgressArrays();
    const player: any = {
      ...this.me,
      // Strip validated photo points — server re-adds them on reconcile.
      points: this.getLocallyEarnedPoints(),
    };
    // Never send empty placeholder arrays: an empty questionStatuses / questionAnswers
    // wipes the server-side statuses and breaks the admin "From correct answers" total.
    if (!Array.isArray(player.questionStatuses) || player.questionStatuses.length === 0) {
      delete player.questionStatuses;
    }
    if (!Array.isArray(player.questionAnswers) || player.questionAnswers.length === 0) {
      delete player.questionAnswers;
    }
    return player;
  }

  /** UI total; never show negative while local/server snapshots are briefly out of sync. */
  getDisplayPoints(): number {
    return Math.max(0, Number(this.me?.points || 0));
  }

  private validatedPhotoFingerprint(photos: any[]): string {
    return (photos || [])
      .filter((photo) => photo?.isValidated === true)
      .map((photo) => `${photo.url}:${Number(photo.points)}`)
      .sort()
      .join('|');
  }

  hasValidatedPhotosChanged(localPhotos: any[], remotePhotos: any[]): boolean {
    return this.validatedPhotoFingerprint(localPhotos) !== this.validatedPhotoFingerprint(remotePhotos);
  }


  getMobileOperatingSystem() {
    var userAgent = navigator.userAgent || navigator.vendor;
  
        // Windows Phone must come first because its UA also contains "Android"
      if (/windows phone/i.test(userAgent)) {
          return "Windows Phone";
      }
  
      if (/android/i.test(userAgent)) {
          return "Android";
      }
  
      // iOS detection from: http://stackoverflow.com/a/9039885/177710
      if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
          return "iOS";
      }
  
      return "unknown";
  }

  validateDevice(){
    // Check for location access, check for internet access
    // If problem -> Redirect to error page
  }

  updateMe(){

  }
  constructor() { }


}
